import React, { useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import useQueryParams from "../../../../../../hooks/useQueryParams";
import {
  setChoices,
  setFeedback,
  setIsAnsweredCorrectly,
  setIsModalOpen,
  setSelectedBlank,
  setSelectedChoice,
} from "../../../../../../redux/chapter/chapterOptions/chapterOptions";
import {
  saveBlankProgress,
  updateProgress,
} from "../../../../../../redux/chapter/chapterSlice";
import images from "../../../../../../utilities/images/images";
import BottomModal from "../BottomModal/BottomModal";
import CompletionModal from "../CompletionModal/CompletionModal";
import "./BottomModalWrapper.css";

/* ---------- Helper: Scroll to next blank inside the correct container ---------- */
function scrollToElementInsideContainer(container, element) {
  if (!container || !element) return;

  const containerRect = container.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();

  // Calculate offset relative to container
  const offset = elementRect.top - containerRect.top;

  // Scroll within container if it can scroll, otherwise use window
  if (container.scrollHeight > container.clientHeight + 10) {
    container.scrollTo({
      top: container.scrollTop + offset - container.clientHeight / 3,
      behavior: "smooth",
    });
  } else {
    // Fallback: scroll window
    window.scrollTo({
      top:
        window.scrollY +
        elementRect.top -
        window.innerHeight / 2 +
        elementRect.height / 2,
      behavior: "smooth",
    });
  }
}

const BottomModalWrapper = ({
  isModalOpen,
  containerRef,
  chapterInfo,
  onAction,
  darkMode,
}) => {
  const dispatch = useDispatch();
  const { selectedBlank, choices, selectedChoice, feedback } = useSelector(
    (state) => state.options
  );
  const { user_progress } = useSelector((state) => state.chapter);
  const params = useQueryParams();
  const user_id = params.get("user_id");

  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completionData, setCompletionData] = useState({ score: 0, total: 0 });
  const [hasCheckedAnswer, setHasCheckedAnswer] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const isAllAnswered = useMemo(() => {
    const total = chapterInfo?.total_qn_blanks || 0;
    const answeredCount = user_progress.filter(
      (item) => item.selected_option_index !== null
    ).length;
    return answeredCount === total;
  }, [user_progress, chapterInfo]);

  React.useEffect(() => {
    setHasCheckedAnswer(false);
  }, [selectedBlank]);

  /* ---------- Handle choice selection ---------- */
  const handleChoiceSelect = (choice) => {
    if (!selectedBlank || !containerRef.current || hasCheckedAnswer) return;

    dispatch(setSelectedChoice(choice));

    const blankElement = containerRef.current.querySelector(
      `.blank[blank_id="${selectedBlank}"]`
    );
    if (!blankElement) return;

    const answer = blankElement.getAttribute("data-answer");
    const selectedIndex = choices.indexOf(choice);
    const isCorrect = choice === answer;

    setHasCheckedAnswer(true);

    dispatch(
      updateProgress({
        qn_blank_id: parseInt(selectedBlank, 10),
        is_correct: isCorrect,
        selected_option_index: selectedIndex + 1,
      })
    );

    dispatch(
      saveBlankProgress({
        user_id,
        chapter_id: chapterInfo.chapter_id,
        qn_blank_id: parseInt(selectedBlank, 10),
        is_correct: isCorrect,
        selected_option_index: selectedIndex + 1,
      })
    );

    dispatch(
      setFeedback({
        user_id,
        icon: isCorrect ? images.Correct : images.Wrong,
        text: isCorrect
          ? "Great! Correct Answer."
          : "Incorrect Answer! Choose another option.",
        type: isCorrect ? "correct" : "wrong",
      })
    );

    dispatch(setIsAnsweredCorrectly(isCorrect));
  };

  /* ---------- FIXED: Continue Button Scroll + Focus Next Blank ---------- */
  const handleContinue = () => {
    if (!containerRef.current || !selectedBlank) return;

    dispatch(setFeedback(""));
    dispatch(setIsAnsweredCorrectly(false));
    dispatch(setSelectedChoice(""));
    setHasCheckedAnswer(false);

    const blanks = Array.from(
      containerRef.current.querySelectorAll(".blank-wrap")
    );
    const currentIndex = blanks.findIndex((b) =>
      b.querySelector(`.blank[blank_id="${selectedBlank}"]`)
    );

    if (currentIndex === -1) return;

    // Find next unattempted or incorrect blank
    let nextBlankWrap = blanks.slice(currentIndex + 1).find((b) => {
      const blank = b.querySelector(".blank");
      if (!blank) return false;
      const id = parseInt(blank.getAttribute("blank_id"), 10);
      const progress = user_progress.find((p) => p.qn_blank_id === id);
      return (
        !progress || progress.selected_option_index === null || progress.is_correct === 0
      );
    });

    // If not found ahead, wrap around from start
    if (!nextBlankWrap) {
      nextBlankWrap = blanks.find((b) => {
        const blank = b.querySelector(".blank");
        const id = parseInt(blank.getAttribute("blank_id"), 10);
        const progress = user_progress.find((p) => p.qn_blank_id === id);
        return (
          !progress || progress.selected_option_index === null || progress.is_correct === 0
        );
      });
    }

    if (nextBlankWrap) {
      // Scroll smoothly inside container or window
      scrollToElementInsideContainer(containerRef.current, nextBlankWrap);

      // Wait for scroll animation before triggering click
      setTimeout(() => {
        if (nextBlankWrap._clickHandler) {
          nextBlankWrap._clickHandler();
        } else {
          nextBlankWrap.dispatchEvent(
            new MouseEvent("click", { bubbles: true })
          );
        }

        // Highlight to confirm visually
        nextBlankWrap.style.outline = "2px dashed #007bff";
        setTimeout(() => (nextBlankWrap.style.outline = "none"), 2000);
      }, 500);
    } else {
      handleCloseModal();
    }

    if (onAction) onAction({ type: "continue_blank" });
  };

  /* ---------- Submit practice ---------- */
  const handleSubmitPractice = () => {
    const total = chapterInfo?.total_qn_blanks || 0;
    const score = user_progress.filter((item) => item.is_correct === 1).length;
    setCompletionData({ score, total });
    setShowCompletionModal(true);
    setIsCollapsed(true);
    if (onAction) onAction({ type: "completed", message: "Completed" });
  };

  /* ---------- Close modal ---------- */
  const handleCloseModal = () => {
    dispatch(setIsModalOpen(false));
    dispatch(setSelectedBlank(null));
    dispatch(setChoices([]));
    dispatch(setSelectedChoice(""));
    dispatch(setFeedback(""));
    dispatch(setIsAnsweredCorrectly(false));
    setHasCheckedAnswer(false);
    setShowCompletionModal(false);
  };

  /* ---------- Render ---------- */
  return (
    <>
      {isModalOpen && (
        <BottomModal
          isOpen={!isCollapsed}
          onToggle={() => {
            if (!isCollapsed) {
              handleCloseModal();
            }
            setIsCollapsed((prev) => !prev);
          }}
          feedback={feedback}
        >
          <div className={`modal-inner ${darkMode ? "dark" : "light"}`}>
            <div className="choices-grid">
              {choices.map((choice) => {
                const isSelected = selectedChoice === choice;
                const progressForBlank = user_progress.find(
                  (p) => p.qn_blank_id === parseInt(selectedBlank, 10)
                );
                const isAnswered =
                  progressForBlank && progressForBlank.selected_option_index;

                const correctAnswer = containerRef.current
                  ?.querySelector(`.blank[blank_id="${selectedBlank}"]`)
                  ?.getAttribute("data-answer");

                const getClassName = () => {
                  if (!isAnswered) {
                    return isSelected ? "selected-initial" : "";
                  }

                  if (isSelected) {
                    return progressForBlank.is_correct === 1
                      ? "correct-choice"
                      : "wrong-choice";
                  }

                  if (!isSelected && choice === correctAnswer) {
                    return "highlight-correct";
                  }

                  return "disabled-choice";
                };

                return (
                  <button
                    key={choice}
                    className={`choice-btn ${getClassName()}`}
                    onClick={() => {
                      if (!isAnswered) handleChoiceSelect(choice);
                    }}
                    disabled={Boolean(isAnswered)}
                  >
                    {choice}
                  </button>
                );
              })}

              {isAllAnswered ? (
                <button
                  className="modal-action-btn submit"
                  onClick={handleSubmitPractice}
                >
                  Submit Practice
                </button>
              ) : (
                hasCheckedAnswer && (
                  <button
                    className="modal-action-btn continue"
                    onClick={handleContinue}
                  >
                    Continue
                  </button>
                )
              )}
            </div>

            {/* Mobile version buttons */}
            {isAllAnswered ? (
              <button
                className="modal-action-btn-mobile submit"
                onClick={handleSubmitPractice}
              >
                Submit Practice
              </button>
            ) : (
              hasCheckedAnswer && (
                <button
                  className="modal-action-btn-mobile continue"
                  onClick={handleContinue}
                >
                  Continue
                </button>
              )
            )}
          </div>
        </BottomModal>
      )}

      <CompletionModal
        isOpen={showCompletionModal}
        onClose={handleCloseModal}
        score={completionData.score}
        total={completionData.total}
        darkMode={darkMode}
      />
    </>
  );
};

export default BottomModalWrapper;
